import bs58 from "bs58";
import { signSolanaMessage, signSolanaTransaction } from "../auth/turnkey.ts";
import { b64, bytesOf, signedForSubmission, throwaway } from "./sign.ts";
import type { WalletHandle } from "./wallets.ts";

/**
 * ⚠️ ONE SIGNER INTERFACE, SO NOTHING DOWNSTREAM ASKS WHOSE KEY IT IS.
 *
 * The app has two kinds of signer and they behave nothing alike. Turnkey holds the key for us and signs the
 * BYTES it is handed, with a session stamp and no prompt — the user sees nothing. An extension wallet is a
 * separate, sleepy process that parses the transaction, may rewrite it, and asks the user every time.
 *
 * Every difference between them is absorbed here. A trade flow should read the same whichever one is
 * connected, because the thing it is doing IS the same: get a signature over a transaction we built.
 */
export interface Signer {
  readonly kind: "turnkey" | "wallet";
  /** What to call it in the interface — "your Kinetiq wallet", "Phantom". */
  readonly label: string;
  readonly address: string;
  /**
   * The turnkey sub-organization this key lives in, and NULL for an external wallet, whose key lives in an
   * extension. The session records it so a later signature can be requested without a second lookup; a
   * wallet session records nothing, because the extension must be reconnected anyway.
   */
  readonly subOrgId: string | null;
  /** base58 ed25519 signature over the message, which is what the session challenge expects. */
  signMessage(message: string): Promise<string>;
  /**
   * Sign transactions, base64 in and base64 out.
   *
   * `pristine` says these bytes must reach a party that will REFUSE a modified transaction — jupiter, for
   * a deposit or a withdrawal. It is not a preference: a wallet that rewrites the message produces a
   * signature valid only over its own version, and jupiter answers `Transaction accounts modified`. What it
   * costs is a second simulation in the wallet, so it is asked for rather than assumed.
   */
  signTransactions(unsigned: readonly string[], pristine?: boolean): Promise<string[]>;
}

export const turnkeySigner = (subOrgId: string, address: string): Signer => ({
  kind: "turnkey",
  label: "your Kinetiq wallet",
  address,
  subOrgId,
  signMessage: (message) => signSolanaMessage(subOrgId, address, message),
  // ⚠️ `pristine` IS IGNORED, AND CORRECTLY. Turnkey is handed hex and returns hex; it never deserialises
  // into an instruction list, so it has nothing to add and no message to recompile. Measured 2026-08-15: a
  // 3-signature transaction came back 375 bytes for 375, message byte-identical, only our slot filled.
  signTransactions: async (unsigned) => {
    const signed: string[] = [];
    for (const one of unsigned) signed.push(await signSolanaTransaction(subOrgId, address, one));
    return signed;
  },
});

/**
 * ⚠️ A WALLET ASKED TO SIGN EXACTLY ONE TRANSACTION REWRITES IT.
 *
 * CONFIRMED 2026-09-18 against phantom: a jupiter withdrawal rewritten four times as a lone signature came
 * back byte-identical when a second transaction rode along, and landed. The guards it appends assert a
 * simulated outcome, and the second of two dependent transactions cannot be simulated without applying the
 * first — so a batch is something the simulator cannot speak about, and it adds nothing.
 *
 * ⚠️ UNDOCUMENTED WALLET BEHAVIOUR. It is load-bearing for every jupiter transaction an external wallet
 * signs here, and it can change without notice. `signedForSubmission` is what notices: it verifies the
 * signature against our bytes before anything is sent, and falls back to the wallet's own bytes when those
 * are the only ones the signature is valid over.
 */
export const walletSigner = (handle: WalletHandle, address: string): Signer => ({
  kind: "wallet",
  label: handle.name,
  address,
  subOrgId: null,

  signMessage: async (message) =>
    bs58.encode(await handle.signMessage(new TextEncoder().encode(message))),

  signTransactions: async (unsigned, pristine = false) => {
    const mine = unsigned.map(bytesOf);
    const pairing = pristine && mine.length === 1 && handle.signsBatches;
    const sending = pairing ? [...mine, throwaway(mine[0] as Uint8Array, address)] : mine;

    const answered = await handle.signTransactions(sending);
    const signed: string[] = [];
    for (let index = 0; index < mine.length; index += 1) {
      const theirs = answered[index];
      if (theirs === undefined)
        throw new Error(`${handle.name} returned no signature for this transaction`);
      signed.push(await signedForSubmission(b64(mine[index] as Uint8Array), theirs, address));
    }
    return signed;
  },
});

/**
 * Sign exactly one transaction. Every trade flow signs one at a time, and a destructured array leaves the
 * caller holding `string | undefined` for a case that cannot happen — so the absence is checked once, here,
 * and never re-litigated with a cast at each site.
 */
export const signOne = async (
  signer: Signer,
  unsigned: string,
  pristine: boolean,
): Promise<string> => {
  const [signed] = await signer.signTransactions([unsigned], pristine);
  if (signed === undefined) throw new Error(`${signer.label} returned no signed transaction`);
  return signed;
};
